const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL;

export interface UserSettings {
  id?: number;
  userId?: number;
  timezone: string;
  language: string;
  theme: 'light' | 'dark' | 'system';
  pushEnabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Get user settings from backend
 */
export const getUserSettings = async (): Promise<UserSettings | null> => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/user/settings`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      console.error('Failed to fetch settings:', response.status);
      return null;
    }

    const settings = await response.json();
    return settings;
  } catch (error) {
    console.error('Error fetching settings:', error);
    return null;
  }
};

/**
 * Update user settings on backend
 */
export const updateUserSettings = async (settings: Partial<UserSettings>): Promise<UserSettings | null> => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/user/settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      console.error('Failed to update settings:', response.status);
      return null;
    }

    const updatedSettings = await response.json();
    return updatedSettings;
  } catch (error) {
    console.error('Error updating settings:', error);
    return null;
  }
};

/**
 * Save theme preference and apply it
 */
export const saveThemePreference = async (theme: 'light' | 'dark' | 'system'): Promise<boolean> => {
  try {
    // Save to backend
    const result = await updateUserSettings({ theme, timezone: '', language: '', pushEnabled: false });
    
    if (result) {
      // Apply theme immediately
      applyTheme(theme);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error saving theme:', error);
    return false;
  }
};

/**
 * Save language preference
 */
export const saveLanguagePreference = async (language: string): Promise<boolean> => {
  try {
    const result = await updateUserSettings({ language, timezone: '', theme: 'system', pushEnabled: false });
    return result !== null;
  } catch (error) {
    console.error('Error saving language:', error);
    return false;
  }
};

/**
 * Apply theme to the UI
 */
export const applyTheme = (theme: 'light' | 'dark' | 'system') => {
  if (theme === 'system') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute('data-joy-color-scheme', prefersDark ? 'dark' : 'light');
  } else {
    document.documentElement.setAttribute('data-joy-color-scheme', theme);
  }
  
  // Store in localStorage as fallback
  localStorage.setItem('userTheme', theme);
};

/**
 * Load and apply user preferences on app start
 */
export const loadUserPreferences = async () => {
  try {
    const settings = await getUserSettings();
    
    if (settings) {
      // Apply theme
      if (settings.theme) {
        applyTheme(settings.theme);
      }
      
      // Apply language (you can expand this based on your i18n setup)
      if (settings.language) {
        localStorage.setItem('userLanguage', settings.language);
      }
      
      return settings;
    }
    
    // Fallback to localStorage if backend fails
    const storedTheme = localStorage.getItem('userTheme') as 'light' | 'dark' | 'system' | null;
    if (storedTheme) {
      applyTheme(storedTheme);
    }
    
    return null;
  } catch (error) {
    console.error('Error loading preferences:', error);
    return null;
  }
};
